import { useEffect, useMemo } from 'react'
import { useDebounce } from 'use-debounce'

import { ContactFormContent } from 'clickNgo/entities/contact'
import { TContactGeneralForm } from 'clickNgo/entities/contact'
import { useGetFormData, useGetFormDataField } from 'cng-common/features/form'

import { ContactEditorFormKeys } from '../configs'
import { ContactEditorSettingFormFields } from '../models'

export function useValidateForm() {
  const [, setErrorMessages] = useGetFormDataField<string[]>(
    ContactEditorFormKeys.settings,
    ContactEditorSettingFormFields.ErrorMessages
  )

  const [generalFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.general)
  const [commonFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.common)
  const [hpRolesFormData] = useGetFormData<TContactGeneralForm>(ContactEditorFormKeys.hpRoles)
  const [relinkingData] = useGetFormData<TContactGeneralForm>(
    ContactEditorFormKeys.contactRelinking
  )

  const modifiedFields = useMemo(
    () => [
      ...generalFormData.modifiedFields,
      ...commonFormData.modifiedFields,
      ...hpRolesFormData.modifiedFields,
      ...relinkingData.modifiedFields,
    ],
    [
      commonFormData.modifiedFields,
      generalFormData.modifiedFields,
      hpRolesFormData.modifiedFields,
      relinkingData.modifiedFields,
    ]
  )

  const [debouncedModifiedFields] = useDebounce(modifiedFields, 500)

  const invalidFields = useMemo(() => {
    const invalidModifiedFields = [
      ...Object.keys(generalFormData.invalidFields),
      ...Object.keys(commonFormData.invalidFields),
      ...Object.keys(hpRolesFormData.invalidFields),
    ].filter(
      (el) =>
        debouncedModifiedFields.includes(el) ||
        // Phone code exceptions
        el === ContactFormContent.fields.workPhoneCode.field ||
        el === ContactFormContent.fields.mobilePhoneCode.field
    )

    return [...invalidModifiedFields, ...Object.keys(relinkingData.invalidFields)]
  }, [
    commonFormData.invalidFields,
    debouncedModifiedFields,
    generalFormData.invalidFields,
    hpRolesFormData.invalidFields,
    relinkingData.invalidFields,
  ])

  const validationData: Record<string, string> = useMemo(
    () => ({
      ...generalFormData.invalidFields,
      ...commonFormData.invalidFields,
      ...hpRolesFormData.invalidFields,
      ...relinkingData.invalidFields,
    }),
    [
      commonFormData.invalidFields,
      generalFormData.invalidFields,
      hpRolesFormData.invalidFields,
      relinkingData.invalidFields,
    ]
  )

  const hasInvalidData = Boolean(invalidFields.length)
  const hasModifiedValues = Boolean(modifiedFields.length)

  useEffect(() => {
    if (hasInvalidData) {
      const messages = invalidFields
        .map((field) => `${field}: ${validationData[field]}`)
        .filter(Boolean)
      setErrorMessages(messages)
    } else {
      setErrorMessages([])
    }
  }, [hasInvalidData, invalidFields, setErrorMessages, validationData])

  return { hasInvalidData, hasModifiedValues, invalidFields, modifiedFields }
}
